import moment from "moment";
import { NewLineRegex, TabRegex } from "./constants";

const getHoursMinutes = (startTime: moment.Moment, endTime: moment.Moment) => {
  // calculate total duration
  var duration: any = moment.duration(endTime.diff(startTime));

  // duration in hours
  var hours = parseInt(duration.asHours());

  // duration in minutes
  var minutes = parseInt(duration.asMinutes()) % 60;

  return { hours, minutes };
};
export const getTimeDiff = (date: any, oldDate = true) => {
  if (oldDate) {
    return moment().diff(moment(date), "minutes") < 60
      ? `${moment().diff(moment(date), "minutes")} ${moment().diff(moment(date), "minutes") > 1 ? "MINUTES" : "MINUTE"}`
      : moment().diff(moment(date), "hours") < 25
      ? `${moment().diff(moment(date), "hours")} ${moment().diff(moment(date), "hours") > 1 ? "HOURS" : "HOUR"}`
      : `${moment().diff(moment(date), "days")} ${moment().diff(moment(date), "days") > 1 ? "DAYS" : "DAY"}`;
  } else {
    return moment(date).diff(moment(), "minutes") < 60
      ? `${moment(date).diff(moment(), "minutes")} ${moment(date).diff(moment(), "minutes") > 1 ? "MINUTES" : "MINUTE"}`
      : moment().diff(moment(date), "hours") < 25
      ? `${getHoursMinutes(moment(), moment(date)).hours} : ${getHoursMinutes(moment(), moment(date)).minutes} ${moment(date).diff(moment(), "hours") > 1 ? "HOURS" : "HOUR"}`
      : `${moment(date).diff(moment(), "days")} ${moment(date).diff(moment(), "days") > 1 ? "DAYS" : "DAY"}`;
  }
};

/**
 * Matches mobile post/live timestamps.
 * - < 60m: "N MINUTE(S) AGO"
 * - < 24h: "N HOUR(S) AGO"
 * - yesterday: "Yesterday 14:30"
 * - same year: "June 6"
 * - other year: "June 6, 2025"
 */
export const formatPostTime = (date: any): string => {
  const created = moment(date);
  if (!created.isValid()) return "";

  const now = moment();
  const minutes = Math.max(0, now.diff(created, "minutes"));
  const hours = Math.max(0, now.diff(created, "hours"));

  if (hours < 24) {
    if (minutes < 60) {
      return `${minutes} ${minutes === 1 ? "MINUTE" : "MINUTES"} AGO`;
    }
    return `${hours} ${hours === 1 ? "HOUR" : "HOURS"} AGO`;
  }

  if (created.isSame(now.clone().subtract(1, "day"), "day")) {
    return `Yesterday ${created.format("HH:mm")}`;
  }

  if (created.isSame(now, "year")) {
    return created.format("MMMM D");
  }

  return created.format("MMMM D, YYYY");
};

/**
 * Receiving value argument for checking whether it has any value or not
 * @param value // any value
 * @returns // boolean
 */
export const isEmpty = (value: any) => {
  let isEmpty = false;
  if (!value) {
    isEmpty = true;
  } else if (typeof value == "undefined") {
    isEmpty = true;
  } else if (value.toString().trim().length === 0) {
    isEmpty = true;
  } else if (typeof value === "object" && Object.keys(value).length === 0) {
    isEmpty = true;
  }
  return isEmpty;
};

/**
 * Finds the code of the emoji by title
 * @param title // emoji title
 * @returns // code for the emoji
 */
export const getEmoji = (title: string) => {
  let result: any;
  switch (title) {
    case "like":
      result = <span className="reaction-icon">&#128077;</span>;
      break;
    case "heart":
      result = <span className="reaction-icon">&#128151;</span>;
      break;
    case "laugh":
      result = <span className="reaction-icon">&#128515;</span>;
      break;
    case "angry":
      result = <span className="reaction-icon">&#128545;</span>;
      break;
    default:
      result = <span className="reaction-icon">&#128077;</span>;
      break;
  }
  return result;
};

/**
 * Method to capitalize a string's first character
 * @param string any string
 * @returns The capitalized string
 */
export const capitalize = (string: string) => {
  return string?.toLowerCase()?.replace(/\b[a-z]/g, function (letter) {
    return letter?.toUpperCase();
  });
};

export const createVideoPosterFromBlob = (file: Blob): Promise<string | undefined> => {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    let settled = false;

    const cleanup = () => {
      try {
        URL.revokeObjectURL(url);
      } catch {}
    };

    const done = (poster?: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(poster);
    };

    const tryDraw = () => {
      if (video.readyState < 2) return; // HAVE_CURRENT_DATA
      if (!video.videoWidth || !video.videoHeight) return;
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return done(undefined);
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return done(undefined);
            try {
              const posterUrl = URL.createObjectURL(blob);
              return done(posterUrl);
            } catch {
              return done(undefined);
            }
          },
          "image/jpeg",
          0.85
        );
        return;
      } catch {
        return done(undefined);
      }
    };

    video.preload = "metadata";
    (video as any).playsInline = true;
    video.muted = true;
    video.src = url;

    video.addEventListener("error", () => done(undefined));
    video.addEventListener("loadedmetadata", () => {
      try {
        const t = video.duration && isFinite(video.duration) ? Math.min(0.1, video.duration / 2) : 0.1;
        video.currentTime = t;
      } catch {
        tryDraw();
      }
    });
    video.addEventListener("seeked", tryDraw);
    video.addEventListener("loadeddata", tryDraw);

    setTimeout(() => done(undefined), 1500);
  });
};

export const createVideoPosterBlobFromBlob = (
  file: Blob,
  opts?: { maxWidth?: number; maxHeight?: number; mimeType?: string; quality?: number; seekSeconds?: number; timeoutMs?: number }
): Promise<Blob | undefined> => {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    let settled = false;

    const maxWidth = typeof opts?.maxWidth === "number" && opts.maxWidth > 0 ? opts.maxWidth : 720;
    const maxHeight = typeof opts?.maxHeight === "number" && opts.maxHeight > 0 ? opts.maxHeight : 720;
    const mimeType = typeof opts?.mimeType === "string" && opts.mimeType ? opts.mimeType : "image/jpeg";
    const quality = typeof opts?.quality === "number" ? opts.quality : 0.85;
    const seekSeconds = typeof opts?.seekSeconds === "number" && opts.seekSeconds >= 0 ? opts.seekSeconds : 0.1;
    const timeoutMs = typeof opts?.timeoutMs === "number" && opts.timeoutMs > 0 ? opts.timeoutMs : 2000;

    const cleanup = () => {
      try {
        URL.revokeObjectURL(url);
      } catch {}
    };

    const done = (posterBlob?: Blob) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(posterBlob);
    };

    const tryDraw = () => {
      if (video.readyState < 2) return; // HAVE_CURRENT_DATA
      if (!video.videoWidth || !video.videoHeight) return;

      const srcW = video.videoWidth;
      const srcH = video.videoHeight;
      const scale = Math.min(1, maxWidth / srcW, maxHeight / srcH);
      const dstW = Math.max(1, Math.round(srcW * scale));
      const dstH = Math.max(1, Math.round(srcH * scale));

      const canvas = document.createElement("canvas");
      canvas.width = dstW;
      canvas.height = dstH;
      const ctx = canvas.getContext("2d");
      if (!ctx) return done(undefined);
      try {
        ctx.drawImage(video, 0, 0, dstW, dstH);
        canvas.toBlob(
          (blob) => {
            if (!blob) return done(undefined);
            return done(blob);
          },
          mimeType,
          quality
        );
        return;
      } catch {
        return done(undefined);
      }
    };

    video.preload = "metadata";
    (video as any).playsInline = true;
    video.muted = true;
    video.src = url;

    video.addEventListener("error", () => done(undefined));
    video.addEventListener("loadedmetadata", () => {
      try {
        const t = video.duration && isFinite(video.duration) ? Math.min(seekSeconds, video.duration / 2) : seekSeconds;
        video.currentTime = t;
      } catch {
        tryDraw();
      }
    });
    video.addEventListener("seeked", tryDraw);
    video.addEventListener("loadeddata", tryDraw);

    setTimeout(() => done(undefined), timeoutMs);
  });
};

export const createVideoPosterFromSrc = async (src: string | Blob): Promise<string | undefined> => {
  try {
    if (src instanceof Blob) return await createVideoPosterFromBlob(src);
    if (!src) return undefined;
    if (src.startsWith("data:") || src.startsWith("blob:")) {
      const blob = await fetch(src).then((r) => r.blob());
      return await createVideoPosterFromBlob(blob);
    }
    return undefined;
  } catch {
    return undefined;
  }
};

export const getVideoDurationSecondsFromBlob = (file: Blob): Promise<number | undefined> => {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    let settled = false;

    const cleanup = () => {
      try {
        URL.revokeObjectURL(url);
      } catch {}
    };

    const done = (duration?: number) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(typeof duration === "number" && isFinite(duration) ? duration : undefined);
    };

    video.preload = "metadata";
    (video as any).playsInline = true;
    video.muted = true;
    video.src = url;

    video.addEventListener("loadedmetadata", () => done(video.duration));
    video.addEventListener("error", () => done(undefined));
    setTimeout(() => done(undefined), 1500);
  });
};

export const primeVideoFirstFrame = (video: HTMLVideoElement) => {
  try {
    const t = video.duration && isFinite(video.duration) ? Math.min(0.1, video.duration / 2) : 0.1;
    video.currentTime = t;
  } catch {}
};

export const handleVideoPrimeOnLoadedMetadata = (e: any) => {
  const v = e.currentTarget as HTMLVideoElement;
  primeVideoFirstFrame(v);
};

export const handleVideoPauseOnSeeked = (e: any) => {
  try {
    (e.currentTarget as HTMLVideoElement).pause();
  } catch {}
};

/**
 * Creates a stable, memory-backed image Blob to avoid Chrome issues like
 * net::ERR_UPLOAD_FILE_CHANGED when the original disk-backed File changes.
 *
 * IMPORTANT: This clones bytes, so it is capped by maxBytes to avoid large memory usage.
 */
export const createStableImageBlob = async (file: Blob, maxBytes: number = 8 * 1024 * 1024): Promise<Blob> => {
  try {
    const type = (file as any)?.type || "";
    if (!type.startsWith("image/")) return file;
    if (typeof file.size === "number" && file.size > maxBytes) return file;

    const ab = await (file as any).arrayBuffer?.();
    if (!ab) return file;

    const ct = type || "application/octet-stream";
    const nm = (file as any)?.name;
    // Prefer returning a real Blob. Some runtimes/polyfills expose a `File` constructor that
    // does NOT produce a native Blob/File (breaking URL.createObjectURL and uploads).
    // Only use `new File` if it actually yields a Blob in this runtime.
    if (typeof File !== "undefined" && typeof nm === "string" && nm) {
      try {
        const f: any = new File([ab], nm, { type: ct });
        if (f instanceof Blob) return f as Blob;
      } catch {}
    }
    return new Blob([ab], { type: ct });
  } catch {
    return file;
  }
};

/**
 * Infer media kind from a src string.
 * Supports `blob:` preview URLs, `https:` URLs, and data URLs (e.g. generated posters).
 */
export const getMediaKindFromSrc = (src?: string): "image" | "video" | "other" => {
  if (!src) return "other";

  // data:[mime];...
  if (src.startsWith("data:")) {
    const mime = src.slice(5, src.indexOf(";"));
    if (mime.startsWith("image/")) return "image";
    if (mime.startsWith("video/")) return "video";
    return "other";
  }

  const lower = src.toLowerCase();
  if (/\.(mp4|mov|wmv|avi|webm|mpeg|flv|hevc|m4v|quicktime)(\?|#|$)/.test(lower)) return "video";
  if (/\.(png|jpe?g|gif|webp|heic|heif|bmp)(\?|#|$)/.test(lower)) return "image";
  return "other";
};

export const isJsonString = (userData) => {
  try {
    JSON.parse(userData);
  } catch (e) {
    return false;
  }
  return true;
};

export const otherText = (text: string) => (
  <div
    style={{
      height: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    <p>{text ? text : ""}</p>
  </div>
);

export const utcHoursDifference = (date: any) => {
  const localOffsetInHours = -(moment().utcOffset() / 60); // old code
  // const localOffsetInHours = (moment().utcOffset() / 60); // new code
  const convert_date = moment(date).clone().add(localOffsetInHours, "hours").toDate();
  return convert_date;
};

// export  const calculateDateDifferences = (start: any, end: any) => {
//   const startDate = moment(start);
//   const endDate = moment(end);
//   return (endDate.diff(startDate, "days") + 1);
// };
export const calculateDateDifferences = (date1: any, date2: any) => {
  const start = moment(date1).format("YYYY-MM-DD");
  const end = moment(date2).format("YYYY-MM-DD");

  const getDaysDiff = (start_date, end_date, date_format = "YYYY-MM-DD") => {
    const getDateAsArray = (date) => {
      return moment(date.split(/\D+/), date_format);
    };
    return getDateAsArray(end_date).diff(getDateAsArray(start_date), "days");
  };
  return getDaysDiff(start, end);
};

/**
 * Formats text by converting all newline variations to HTML line breaks
 * @param text - The text to format
 * @returns Formatted text with <br /> tags
 */
export const formatTextWithLineBreaks = (text: string | null | undefined): string => {
  if (!text) return "";
  return text.replace(NewLineRegex, "<br />").replace(TabRegex, "&nbsp;&nbsp;");
};

/**
 * CMS / rich HTML pages already use <p>/<h5>/etc. Do not turn newlines into <br />,
 * or you get huge gaps between paragraphs.
 */
export const formatCmsHtml = (html: string | null | undefined): string => {
  if (!html) return "";
  if (/<[a-z][\s\S]*>/i.test(html)) {
    return html
      .replace(/\\n/g, "\n")
      .replace(/https?:\/\/(?:www\.)?(?:staging\.)?petloverschat\.com(\/(?:privacy-policy|terms-condition|acceptable-policy|csae-policy))/gi, "$1")
      .replace(/>\s+</g, "><")
      .trim();
  }
  return formatTextWithLineBreaks(html);
};

/** Max words for post / live / shorts description text (create + edit). */
export const POST_DESCRIPTION_MAX_WORDS = 500;

export const countWords = (text: string | null | undefined): number => {
  const trimmed = String(text || "").trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).filter(Boolean).length;
};

/** Keep text up to `maxWords` while preserving whitespace between kept words. */
export const clampToMaxWords = (text: string, maxWords: number = POST_DESCRIPTION_MAX_WORDS): string => {
  if (countWords(text) <= maxWords) return text;
  const parts = String(text).match(/\S+|\s+/g) || [];
  let words = 0;
  let out = "";
  for (const part of parts) {
    if (/^\s+$/.test(part)) {
      if (words > 0 && words < maxWords) out += part;
      continue;
    }
    if (words >= maxWords) break;
    out += part;
    words += 1;
  }
  return out;
};

/**
 * Generates a random string of specified length
 * @param length - The length of the random string to generate
 * @returns A random string
 */
export const getRandomString = (length: number) => {
  var result = "";
  var characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};
